
import axios from "axios";
import { getAuth } from "firebase/auth";

import PendingCompanyLevelResponse from "../models/PendingCompanyLevelResponse";

// ============================================================
// CONFIGURATION
// ============================================================

const GET_PENDING_LEVEL_FOR_COMPANY_URL =
  "https://europe-west3-hyper-e122e.cloudfunctions.net/get_pending_level_for_company";

const UPDATE_PROFILE_URL =
  "https://europe-west3-hyper-e122e.cloudfunctions.net/update_profile";

// ============================================================
// PENDING COMPANY LEVEL SERVICE
// ============================================================

class PendingCompanyLevelService {

  // ==========================================================
  // GET PENDING LEVEL FOR COMPANY
  // ==========================================================

  /**
   * Получить первую компанию пользователя,
   * у которой level == null.
   *
   * @param {string} userId - Firebase Auth UID
   * @returns {Promise<PendingCompanyLevelResponse>}
   */
  async getPendingLevelForCompany(userId) {

    try {

      // ======================================================
      // VALIDATION
      // ======================================================

      if (
        typeof userId !== "string" ||
        !userId.trim()
      ) {
        throw new Error(
          "Не передан userId"
        );
      }

      // ======================================================
      // AUTH
      // ======================================================

      const auth = getAuth();

      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error(
          "Пользователь не авторизован"
        );
      }

      const token = await currentUser.getIdToken(true);

      // ======================================================
      // REQUEST
      // ======================================================

      const response = await axios.post(
        GET_PENDING_LEVEL_FOR_COMPANY_URL,
        {
          user_id: userId.trim()
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          timeout: 30000
        }
      );

      // ======================================================
      // PARSE RESPONSE
      // ======================================================

      const result =
        PendingCompanyLevelResponse.fromJson(
          response.data
        );

      // ======================================================
      // BACKEND ERROR
      // ======================================================

      if (!result.success) {
        throw new Error(
          result.error ||
          "Не удалось получить компанию"
        );
      }

      // ======================================================
      // SUCCESS
      // ======================================================

      if (result.hasPendingCompany) {

        console.log(
          "🏢 Найдена компания с неназначенным уровнем:",
          result.companyEmployee
        );

      } else {

        console.log(
          "ℹ️ Компаний с неназначенным уровнем нет"
        );

      }

      return result;

    } catch (error) {

      const errorMessage = axios.isAxiosError(error)
        ? (
            error.response?.data?.error ||
            error.message
          )
        : error.message;

      console.error(
        "❌ Ошибка getPendingLevelForCompany:",
        errorMessage
      );

      throw new Error(
        errorMessage ||
        "Не удалось проверить уровень компании"
      );

    }
  }

  // ==========================================================
  // SET COMPANY EMPLOYEE LEVEL
  // ==========================================================

  /**
   * Назначить уровень сотрудника в компании.
   *
   * Использует существующую Cloud Function update_profile
   * с action = "set_company_employee_level".
   *
   * Доступные уровни:
   * 2 — Руководитель
   * 3 — Сотрудник
   *
   * Уровень 1 (CEO) назначать запрещено.
   *
   * @param {string} relationId - ID документа CompanyEmployees
   * @param {number} level - Уровень 2 или 3
   * @returns {Promise<Object>}
   */
  async setCompanyEmployeeLevel(relationId, level) {

    try {

      // ======================================================
      // VALIDATION
      // ======================================================

      if (
        typeof relationId !== "string" ||
        !relationId.trim() ||
        relationId.includes("/")
      ) {
        throw new Error(
          "Некорректный relationId"
        );
      }

      if (
        !Number.isInteger(level) ||
        ![2, 3].includes(level)
      ) {
        throw new Error(
          "Допустимы только уровни 2 и 3"
        );
      }

      // ======================================================
      // AUTH
      // ======================================================

      const auth = getAuth();

      const currentUser = auth.currentUser;

      if (!currentUser) {
        throw new Error(
          "Пользователь не авторизован"
        );
      }

      const token = await currentUser.getIdToken(true);

      // ======================================================
      // REQUEST
      // ======================================================

      const response = await axios.post(
        UPDATE_PROFILE_URL,
        {
          action: "set_company_employee_level",
          relation_id: relationId.trim(),
          level: level
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          },
          timeout: 30000
        }
      );

      // ======================================================
      // RESPONSE VALIDATION
      // ======================================================

      const result = response.data;

      if (
        !result ||
        result.success !== true ||
        !result.companyEmployee
      ) {
        throw new Error(
          result?.error ||
          "Не удалось обновить уровень сотрудника"
        );
      }

      // ======================================================
      // SUCCESS
      // ======================================================

      console.log(
        "✅ Уровень сотрудника успешно обновлён:",
        result.companyEmployee
      );

      return result;

    } catch (error) {

      // ======================================================
      // ERROR HANDLING
      // ======================================================

      const errorMessage = axios.isAxiosError(error)
        ? (
            error.response?.data?.error ||
            error.message
          )
        : error?.message;

      console.error(
        "❌ Ошибка setCompanyEmployeeLevel:",
        errorMessage
      );

      throw new Error(
        errorMessage ||
        "Не удалось сохранить роль в компании"
      );

    }
  }
}

// ============================================================
// EXPORT
// ============================================================

export default new PendingCompanyLevelService();
